// Licence keys via Lemon Squeezy's public licence API (F13). No backend, no accounts, no API key.
// Client-side gating is bypassable by design: perks are goodwill and convenience, not DRM.
import { LICENCE_API, STORE, loadStore, tierForProduct } from './config';
import type { Tier } from './tier';

export type Entitlement = {
  key: string;
  instanceId: string;
  tier: Exclude<Tier, 'none'>;
  productName: string;
  validatedAt: number;
};

type ApiResponse = {
  activated?: boolean;
  valid?: boolean;
  deactivated?: boolean;
  error?: string | null;
  license_key?: { status?: string; activation_limit?: number; activation_usage?: number };
  instance?: { id: string } | null;
  meta?: { store_id?: number; product_id?: number; product_name?: string };
};

export type LicenceResult = { ok: true; entitlement: Entitlement } | { ok: false; reason: 'invalid' | 'limit' | 'wrong-product' | 'lapsed' | 'network' | 'unconfigured'; message: string };

async function call(action: 'activate' | 'validate' | 'deactivate', body: Record<string, string>): Promise<ApiResponse> {
  const res = await fetch(`${LICENCE_API}/${action}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  });
  // 4xx responses carry a JSON error message we can show; anything else is a network-level failure.
  if (res.status >= 500) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ApiResponse;
}

export function deviceName(ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''): string {
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'device';
  return `Marrow on ${browser} · ${os}`;
}

const LAPSED: Record<string, string> = {
  expired: 'This licence has ended, so supporter perks are off. Anything you downloaded stays until you remove it. Thank you for your support.',
  disabled: 'This licence has been switched off. If you think that’s a mistake, contact us with your receipt.',
  inactive: 'This licence isn’t active on this device any more. Enter the key again to re-activate it.',
};

function checkProduct(r: ApiResponse): { tier: Exclude<Tier, 'none'> } | LicenceResult {
  const tier = r.meta?.store_id === STORE.storeId && r.meta?.product_id ? tierForProduct(r.meta.product_id) : null;
  return tier ? { tier } : { ok: false, reason: 'wrong-product', message: 'That key is for a different product. Check you’ve pasted the key from your Marrow receipt.' };
}

export async function activate(rawKey: string, now = Date.now()): Promise<LicenceResult> {
  await loadStore();
  if (!STORE.storeId) return { ok: false, reason: 'unconfigured', message: 'Unlocking isn’t available yet. Please try again soon.' };
  const key = rawKey.trim();
  if (!/^[A-Za-z0-9-]{8,}$/.test(key)) return { ok: false, reason: 'invalid', message: 'That doesn’t look like a licence key. It’s in your receipt email, and looks like XXXXXXXX-XXXX-….' };
  let r: ApiResponse;
  try {
    r = await call('activate', { license_key: key, instance_name: deviceName() });
  } catch {
    return { ok: false, reason: 'network', message: 'Couldn’t reach the licence server. Check your connection and try again.' };
  }
  if (!r.activated || !r.instance) {
    const limit = /activation limit/i.test(r.error ?? '') || (r.license_key?.activation_limit !== undefined && r.license_key.activation_usage !== undefined && r.license_key.activation_usage >= r.license_key.activation_limit);
    if (limit) {
      const n = r.license_key?.activation_limit ?? 5;
      return { ok: false, reason: 'limit', message: `This key is already in use on ${n} devices, the most it allows. Remove it from a device you no longer use (Support Marrow → Remove from this device), then try again.` };
    }
    if (r.license_key?.status && LAPSED[r.license_key.status]) return { ok: false, reason: 'lapsed', message: LAPSED[r.license_key.status] };
    return { ok: false, reason: 'invalid', message: 'That key wasn’t recognised. Check it against your receipt email.' };
  }
  const p = checkProduct(r);
  if ('ok' in p) {
    // Don't hold a device slot for a key we won't honour.
    await call('deactivate', { license_key: key, instance_id: r.instance.id }).catch(() => undefined);
    return p;
  }
  return { ok: true, entitlement: { key, instanceId: r.instance.id, tier: p.tier, productName: r.meta?.product_name ?? '', validatedAt: now } };
}

/** Re-checks a stored licence. A network failure is reported as such so callers can keep the cached tier. */
export async function validate(ent: Entitlement, now = Date.now()): Promise<LicenceResult> {
  await loadStore();
  let r: ApiResponse;
  try {
    r = await call('validate', { license_key: ent.key, instance_id: ent.instanceId });
  } catch {
    return { ok: false, reason: 'network', message: 'Couldn’t reach the licence server.' };
  }
  const status = r.license_key?.status ?? '';
  if (!r.valid || (status && status !== 'active')) {
    return { ok: false, reason: LAPSED[status] ? 'lapsed' : 'invalid', message: LAPSED[status] ?? LAPSED.inactive };
  }
  const p = checkProduct(r);
  if ('ok' in p) return p;
  return { ok: true, entitlement: { ...ent, tier: p.tier, productName: r.meta?.product_name ?? ent.productName, validatedAt: now } };
}

export async function deactivate(ent: Entitlement): Promise<boolean> {
  try {
    const r = await call('deactivate', { license_key: ent.key, instance_id: ent.instanceId });
    return !!r.deactivated;
  } catch {
    return false;
  }
}
